// src/components/account/AccountSettingsModal.tsx
import React, { useState } from 'react'
import { X, CreditCard, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card, CardContent } from '@/components/ui/Card'
import { useTeacherAuth } from '@/contexts/TeacherAuthContext'
import { CancelSubscriptionFlow } from './CancelSubscriptionFlow'
import { useGeolocation } from '@/hooks/useGeolocation'
import { PRICING } from '@/constants/pricing'

interface AccountSettingsModalProps {
  isOpen: boolean
  onClose: () => void
}

export const AccountSettingsModal: React.FC<AccountSettingsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { profile } = useTeacherAuth()
  const [showCancelFlow, setShowCancelFlow] = useState(false)
  const { isIndia } = useGeolocation()
  const pricing = isIndia ? PRICING.INR : PRICING.USD

  if (!isOpen) return null

  const subscription = profile?.subscription
  const isPremium = subscription?.tier === 'monthly' || subscription?.tier === 'annual'
  const isCanceling = subscription?.cancelAtPeriodEnd

  // Format date
  const formatDate = (date: any) => {
    if (!date) return 'N/A'
    const d = date.toDate ? date.toDate() : new Date(date)
    return d.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
  }

  // Calculate days remaining
  const getDaysRemaining = () => {
    if (!subscription?.accessExpiresAt) return 0
    const end = subscription.accessExpiresAt.toDate 
      ? subscription.accessExpiresAt.toDate() 
      : new Date(subscription.accessExpiresAt)
    const now = new Date()
    const diff = end.getTime() - now.getTime()
    return Math.ceil(diff / (1000 * 60 * 60 * 24))
  }

  // Show cancel flow instead of settings
  if (showCancelFlow) {
    return (
      <CancelSubscriptionFlow
        isOpen={showCancelFlow}
        onClose={() => {
          setShowCancelFlow(false)
          onClose()
        }}
        onBack={() => setShowCancelFlow(false)}
      />
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Account Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Subscription Overview */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Subscription Overview
            </h3>
            <Card>
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Current Plan</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">
                        {isPremium 
                          ? `${subscription.tier.charAt(0).toUpperCase() + subscription.tier.slice(1)} Premium`
                          : 'Free Plan'
                        }
                      </span>
                      {isCanceling && (
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded">
                          Cancels on {formatDate(subscription.accessExpiresAt)}
                        </span>
                      )}
                      {isPremium && !isCanceling && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                          Active
                        </span>
                      )}
                    </div>
                  </div>

                  {isPremium && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                          {isCanceling ? 'Access Until' : 'Next Billing Date'}
                        </span>
                        <span className="font-medium text-gray-900">
                          {formatDate(subscription.accessExpiresAt)}
                        </span>
                      </div>

                      {!isCanceling && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Amount</span>
                          <span className="font-medium text-gray-900">
                            {subscription.tier === 'annual' 
                              ? pricing.annual.display   // Shows $99 or ₹7,999
                              : pricing.monthly.display  // Shows $9.99 or ₹799
                            }/{subscription.tier === 'annual' ? 'year' : 'month'}
                          </span>
                        </div>
                      )}

                      {isCanceling && (
                        <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                          <p className="text-sm text-orange-800">
                            <strong>{getDaysRemaining()} days</strong> of Premium access remaining
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Subscription Details */}
          {isPremium && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                What's Included
              </h3>
              <Card>
                <CardContent className="p-4">
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-center gap-2">
                      <span className="text-green-600">✓</span>
                      Unlimited worksheet generation
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-600">✓</span>
                      All worksheet types & moods
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-600">✓</span>
                      ADHD-friendly adaptive content
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-600">✓</span>
                      Priority support
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
          <Button
            onClick={onClose}
            variant="outline"
            className="w-full"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  )
}